import { hasAgentAccess, hasPlatformAccess, isAdmin, User } from '~api';
import { getAppEnv } from '~portal/env';
import { appMode, AppModes, TAppMode } from './app-mode';

/**
 * @desc The role and permission claims AuthKit derives from the access token. `roles` is `null`
 * and `permissions` empty until the first token refresh resolves, which is why the guard only
 * consults them once `useAuth()` reports a signed-in user.
 */
export type AccessClaims = {
  roles: string[] | null;
  permissions: string[];
};

/** @desc The permission this build's deployment is gated on (design D4). */
export const requiredPermission = (mode: TAppMode): User.Permission =>
  mode === AppModes.Standalone ? User.permissions.AgentAccess : User.permissions.PlatformAccess;

/**
 * @desc Whether the claims grant this deployment. Admins are always in — `internal-admin` carries
 * both access permissions anyway, so the admin arm is belt-and-braces rather than a widening.
 */
export function hasDeploymentAccess(claims: AccessClaims, mode: TAppMode): boolean {
  const principal = { roles: claims.roles ?? [], permissions: claims.permissions };
  if (isAdmin(principal)) return true;
  return mode === AppModes.Standalone ? hasAgentAccess(principal) : hasPlatformAccess(principal);
}

/** @desc Whether the token carries any per-product role, i.e. the backfill has reached this user. */
const carriesProductRole = (roles: string[] | null): boolean =>
  (roles ?? []).some((slug) => User.roleGroupOf(slug) !== null);

/**
 * @desc Whether to show the waiting-approval holding screen instead of the application.
 *
 * With enforcement on (`VITE_ACCESS_ENFORCEMENT_ENABLED`, flipped in step with the backend's
 * `ACCESS_ENFORCEMENT_ENABLED`) the answer is simply "not granted". With it off the guard still
 * holds a user whose token carries per-product role state and not the right permission — that
 * user has been through the backfill, so the holding screen is the honest thing to show them and
 * the backend would refuse their data anyway. What it must never do while the flag is off is hold
 * a user with no per-product roles at all: those are the pre-backfill accounts, and locking them
 * out is precisely the outage the staged rollout exists to avoid.
 *
 * This is UX only. The authoritative gate is the backend's permission middleware.
 */
export function shouldHoldForApproval(
  claims: AccessClaims,
  mode: TAppMode,
  enforced: boolean = getAppEnv().accessEnforcement,
): boolean {
  if (hasDeploymentAccess(claims, mode)) return false;
  if (enforced) return true;
  return carriesProductRole(claims.roles);
}

/**
 * @desc Whether this user is shown the Restricted section (round 3, lane 11, H2-314): the
 * "Restricted" nav group and the `/restricted/*` pages. Three conditions, all required — the
 * `VITE_RESTRICTED_NAV` flag (staging only), the platform build (the standalone build is the chat
 * alone and sends every product route there), and `isAdmin` on the signed-in user's token claims.
 *
 * UX only, like the rest of this module. The edge gate refuses a direct load to anyone else, and
 * anything an experiment fetches comes from admin-only procedures that check `isAdmin` again.
 */
export function canSeeRestricted(
  claims: { roles?: string[] | null; permissions?: string[] | null },
  enabled: boolean = getAppEnv().restrictedNav,
  mode: TAppMode = appMode,
): boolean {
  if (!enabled || mode !== AppModes.Platform) return false;
  return isAdmin({ roles: claims.roles ?? [], permissions: claims.permissions ?? [] });
}
